import { Key, useEffect, useMemo, useRef, useState } from 'react'
import { navigate, PageProps } from 'gatsby'
import { decode as decodeBase64 } from 'base-64'
import Layout from 'components/layouts/tow-column'
import clsx from 'clsx'
import {
  Alert,
  Button,
  CheckboxChangeEvent,
  Error as ErrorComponent,
  Form,
  message,
  SEO,
  showMessage,
  Space,
  TitleAndDescription,
  TitleAndDescriptionFormValues,
} from 'components/common'
import {
  RateStatusSelect,
  StatusFormValue,
  UnavailableItemsCard,
  Variant,
  VariantInputs,
} from 'components/rates'
import { RateVariantUpdateInput, useRate } from 'lib/hooks'
import { time } from 'lib/str-formatter/datetime'
import {
  NotForSaleCreateInput,
  ProductsQuery,
  RateStatus,
  useNotForSaleQuery,
  useProductsLazyQuery,
} from 'lib/graphql/storeadmin'
import { NotForSale } from '../new'
import { ellipsisText } from 'utils/custom-functions'

type FormValues = TitleAndDescriptionFormValues &
  StatusFormValue & {
    variants: Variant[]
  }

type NodeType = {
  key: string
  title: string
  productName: string
  price: number
  inventory: number
}

const onBack = () => navigate('../')

export const Head = () => <SEO title="料金テーブル詳細" />

const IndexPage = ({
  params: { id },
}: PageProps<Queries.Query>): JSX.Element => {
  const [form] = Form.useForm()
  const { rate, error, loading, update, refetch } = useRate(id, {})
  const [nowUpdating, setNowUpdating] = useState(false)
  const [hasUnavailable, setHasUnavailable] = useState(false)
  const [hideWarning, setWarning] = useState(false)

  // Rate error useState
  const [rateErrorDescription, setRateErrorDescription] = useState<
    string | null
  >(null)

  const [storeNotForSale, setStoreNotForSale] = useState<NotForSale[]>([])
  const [searchFiled, setSearchFiled] = useState<string>(null)

  const [getProducts, { data: productData, loading: productsLoading }] =
    useProductsLazyQuery({
      variables: { name_Contains: searchFiled },
    })
  const treeNode = useRef<NodeType[]>([])
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([])
  const [selectedVariants, setSelectedVariants] = useState<NodeType[]>([])
  const [notForSale, setNotForSale] = useState([])
  const [notForSaleProductId, setNotForSaleProductId] = useState([])
  const { data, refetch: notForSaleRefetch } = useNotForSaleQuery({
    variables: { id },
  })
  // Product variants query
  const notForSaleData = useMemo(
    () => data?.notForSale?.products?.edges,
    [data?.notForSale]
  )

  const rateVariants = useMemo<Variant[]>(() => {
    return rate?.variants?.map((variant, i) => ({
      id: variant.id,
      start: time(variant.period.start),
      end: time(variant.period.end),
      rate: Number(variant.rate),
      position: i,
    }))
  }, [rate])

  // waiting for loading data
  useEffect(
    () => {
      const selectItems = []
      const selectProductId = []
      const arrayOfNotForSaleData = []
      // getting details of matched key
      notForSaleData?.forEach((data, index) => {
        selectProductId.push(data?.node?.product.id)
        data?.node?.variantIds?.forEach((key) => {
          data?.node?.product?.variants?.edges.forEach((variant, index) => {
            if (decodeBase64(variant.node.id).split(':')[1] === key) {
              selectItems.push({
                key: variant.node.id,
                title: variant.node.title.jaJp,
                productName: data?.node?.product?.name.jaJp,
              })
              arrayOfNotForSaleData.push(variant.node.id)
            }
          })
        })
      })
      if (selectItems) {
        setNotForSale(arrayOfNotForSaleData)
        setCheckedKeys(arrayOfNotForSaleData)
        setSelectedVariants(selectItems)
        setNotForSaleProductId(selectProductId)
      }
      getProducts()
      refetch({ id })
      notForSaleRefetch({ id })
    },
    /* eslint-disable-next-line */
    [notForSaleData, refetch, notForSaleRefetch]
  )

  useEffect(() => {
    if (selectedVariants.length > 0) setHasUnavailable(true)
  }, [selectedVariants, hasUnavailable])

  const onCheck = (keys: Key[]) => {
    setCheckedKeys(keys)
  }

  const handleSearch = (e) => {
    setSearchFiled(e)
  }

  const openTree = (data: ProductsQuery) => {
    const treeData = []
    const node = []
    data?.products?.edges?.forEach((product) => {
      const children = []
      product.product.variants.edges.forEach((variant) => {
        const child = {
          key: variant.variant.id,
          title: variant.variant.title.jaJp,
          productName: product.product.name.jaJp,
        }
        children.push(child)
        node.push(child)
      })
      treeData.push({
        title: product.product.name.jaJp,
        key: product.product.id,
        children: children,
      })
    })
    treeNode.current = node
    return treeData
  }

  const onHold = () => {
    const selectItems = []
    // Filter Duplicate id and return unique Array
    const filteredCheckedKeys = checkedKeys?.filter(
      (e, i) => checkedKeys?.indexOf(e) === i
    )
    filteredCheckedKeys.forEach((key) => {
      treeNode.current.forEach((node) => {
        if (node.key === key) {
          selectItems.push({
            key: key,
            productName: node.productName,
            title: node.title,
            price: node.price,
            inventory: node.inventory,
          })
        }
      })
    }, selectItems)
    setSelectedVariants(selectItems)
  }

  const onNotForSellDelete = (key: string) => {
    try {
      if (typeof key === 'string') {
        if (selectedVariants.length === 1) {
          setHasUnavailable(false)
          setWarning(false)
        }
        setCheckedKeys((value) => {
          return value.filter((obj) => obj !== key)
        })
        setSelectedVariants((value) => {
          return value.filter((obj) => obj.key !== key)
        })
      }
    } catch (e) {
      showMessage('error', '予期せぬ問題が発生しました。')
    } finally {
      setWarning(true)
      showMessage('success', '削除されました。')
    }
  }

  const onNotForSaleSaved = () => {
    const selectProductVariantId = []
    const selectProductId = []
    const notForSellVariantNode = []

    // Divided Product id and Product variants id
    if (
      productData?.products?.edges?.length > 0 &&
      selectedVariants?.length > 0
    ) {
      selectedVariants?.forEach((selectedVariant) => {
        productData?.products?.edges?.forEach((product) => {
          product?.product?.variants?.edges?.forEach((variant) => {
            if (variant.variant.id === selectedVariant.key) {
              selectProductId.push(product.product.id)
              selectProductVariantId.push(variant.variant.id)
            }
          })
        })
      })
    }

    // Filter Duplicate id and return unique Array
    const filteredSelectProductId = selectProductId?.filter(
      (e, i) => selectProductId?.indexOf(e) === i
    )

    // Format data as API Format
    filteredSelectProductId.forEach((newValue, index) => {
      productData?.products?.edges?.forEach((product) => {
        if (product?.product?.id === filteredSelectProductId[index]) {
          // TODO: Remove decodeBase64 after finish API
          const notForSellId = decodeBase64(product?.product?.id).split(':')[1]
          const notForSellVariant = []

          selectProductVariantId.forEach((variantsValue, i) => {
            product?.product?.variants?.edges?.forEach((variant) => {
              if (variant?.variant?.id === selectProductVariantId[i]) {
                notForSellVariant.push(
                  decodeBase64(variant?.variant?.id).split(':')[1]
                )
              }
            })
          })
          notForSellVariantNode.push({
            id: notForSellId,
            variants: notForSellVariant,
          })
        }
      })
    })
    setStoreNotForSale(notForSellVariantNode)
    showMessage('success', '保存しました。')
  }

  const onUnavailable = (e: CheckboxChangeEvent): void => {
    setHasUnavailable(e.target.checked)
  }

  const onFinish = async (values: FormValues): Promise<void> => {
    try {
      setNowUpdating(true)

      const isNotForSale =
        storeNotForSale?.length === 0 && selectedVariants.length === 0
      if (hasUnavailable && isNotForSale) {
        setRateErrorDescription('利用不可施設を選択してください。')
        throw new Error('利用不可施設を選択してください。')
      }

      const { title, description, variants, status } = values
      const rateVariants = variants.filter((v) => Object.keys(v).length !== 0)

      if (rateVariants.length === 0) {
        setRateErrorDescription(
          'バリエーションは必ず一つ以上設定してください。'
        )
        throw new Error('バリエーションは必ず一つ以上設定してください。')
      }

      const variables = {
        id,
        input: {
          title,
          description,
          variants:
            rateVariants?.map<RateVariantUpdateInput>((variant, index) => ({
              id: variant.id,
              start: time(variant.start),
              end: time(variant.end),
              rate: Number(variant.rate),
              position: variant.position || variants.length + 1,
            })) || [],
          status: status,
          notForSale:
            selectedVariants?.length > 0 || notForSaleProductId?.length === 0
              ? [
                  ...storeNotForSale.map<NotForSaleCreateInput>((value) => ({
                    product: value.id,
                    variantIds: value.variants,
                  })),
                ]
              : [
                  {
                    product: notForSaleProductId[0],
                    variantIds: [],
                  },
                ],
        },
      }
      const { data } = await update({ variables })
      const { rateUserErrors } = data.rateUpdate
      if (rateUserErrors.length > 0) {
        message.error('入力項目に問題があります。')

        rateUserErrors.forEach(({ field }) => {
          switch (field[0]) {
            case 'title':
              setRateErrorDescription('タイトルは必須です。')
              break
            case 'variants':
              setRateErrorDescription(
                'バリエーションは必ず一つ以上設定してください。'
              )
              break
            default:
              break
          }
        })
      } else {
        setRateErrorDescription(null)
        refetch()
        showMessage('success', '保存しました。')
      }
    } catch (e) {
      // captureException(e)
      showMessage('error', '予期せぬ問題が発生しました。')
    } finally {
      setNowUpdating(false)
    }
  }

  const onFinishFailed = () => {
    message.error('入力項目に問題があります。')
  }

  const handleArchiveStatus = async (status: RateStatus) => {
    const { data } = await update({
      variables: {
        id,
        input: { status: status },
      },
    })
    const { rateUserErrors } = data.rateUpdate
    if (rateUserErrors.length > 0) {
      throw new Error('Failed to update status.')
    }
  }

  const archive = async () => {
    try {
      await handleArchiveStatus(RateStatus.Archived)
      message.info('アーカイブしました')
    } catch (e) {
      message.error('アーカイブに失敗しました。')
    }
  }

  const unarchive = async () => {
    try {
      await handleArchiveStatus(RateStatus.Active)
      message.info('アーカイブを解除しました。')
    } catch (e) {
      message.error('アーカイブの解除に失敗しました。')
    }
  }

  if (error) {
    return ErrorComponent.render(error)
  }

  return (
    <>
      <Form
        component="form"
        form={form}
        preserve={false}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Layout
          title={ellipsisText(rate?.title?.jaJp, 12)?.text}
          loading={loading}
          onBack={onBack}
        >
          <Layout.Section size="large">
            {rateErrorDescription !== null && (
              <Alert
                message="エラー"
                description={rateErrorDescription}
                type="error"
                showIcon
              />
            )}

            <Layout.Item>
              <TitleAndDescription
                title={rate?.title?.jaJp}
                description={rate?.description?.jaJp}
                loading={loading}
              />
            </Layout.Item>

            <Layout.Item>
              <VariantInputs initialValue={rateVariants} loading={loading} />
            </Layout.Item>

            <Layout.Item>
              <UnavailableItemsCard
                loading={loading}
                productsLoading={productsLoading}
                hasUnavailable={hasUnavailable}
                notForSaleData={notForSale}
                onUnavailable={onUnavailable}
                productData={productData}
                getProducts={getProducts}
                onHold={onHold}
                openTree={openTree}
                onCheck={onCheck}
                selectedVariants={selectedVariants}
                handleSearch={handleSearch}
                onDelete={onNotForSellDelete}
                onSaved={onNotForSaleSaved}
                hideWarning={hideWarning}
                setWarning={setWarning}
              />
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="small">
            <Layout.Item>
              <RateStatusSelect
                status={rate?.status}
                loading={loading}
                onUnarchive={unarchive}
              />
            </Layout.Item>
            {/* TODO: Phase 1 では非表示 */}
            {/*<Layout.Item>*/}
            {/*  <Card shadow loading={loading}>*/}
            {/*    <div>*/}
            {/*      /!* TODO: products と共通なのでコンポーネント化する *!/*/}
            {/*      <Title level={5}>分類</Title>*/}
            {/*      <Input />*/}
            {/*    </div>*/}

            {/*    <Card.Divider />*/}

            {/*    <div>*/}
            {/*      /!* TODO: products と共通なのでコンポーネント化する *!/*/}
            {/*      <Title level={5}>コレクション</Title>*/}
            {/*      <Input />*/}
            {/*    </div>*/}

            {/*    <Card.Divider />*/}

            {/*    <div>*/}
            {/*      /!* TODO: orders, products と共通なのでコンポーネント化する *!/*/}
            {/*      <div*/}
            {/*        className={clsx(*/}
            {/*          'flex',*/}
            {/*          'justify-between',*/}
            {/*          'items-center',*/}
            {/*          'mb-3'*/}
            {/*        )}*/}
            {/*      >*/}
            {/*        <Title*/}
            {/*          level={5}*/}
            {/*          style={{ marginBottom: 0 }}*/}
            {/*          space={'none'}*/}
            {/*        >*/}
            {/*          タグ*/}
            {/*        </Title>*/}
            {/*        <Button type={'link'}>すべてのタグを表示する</Button>*/}
            {/*      </div>*/}
            {/*      <Input />*/}
            {/*    </div>*/}
            {/*  </Card>*/}
            {/*</Layout.Item>*/}
          </Layout.Section>

          <Layout.Footer
            left={
              <Space>
                {rate?.status === RateStatus.Archived ? (
                  <Button onClick={unarchive}>アーカイブを解除する</Button>
                ) : (
                  <Button onClick={archive}>アーカイブする</Button>
                )}
              </Space>
            }
            right={
              <div className={clsx('flex', 'justify-end')}>
                <Button
                  disabled={hideWarning && selectedVariants.length > 0}
                  type="primary"
                  htmlType="submit"
                  loading={nowUpdating}
                >
                  保存する
                </Button>
              </div>
            }
          />
        </Layout>
      </Form>
    </>
  )
}

export default IndexPage
